"use client";

import React from 'react';
import { motion } from 'framer-motion';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { Icon } from '@iconify/react';
import ServicesAccordion from '@/app/components/ServicesAccordion';
import AlternatingUseCases from '@/app/components/AlternatingUseCases';
import TechSection from '@/components/industry/TechSection';
import { IndustrialsForm } from '@/app/components/IndustrialsForm';

const HospitalityPage = () => {
    const { t, translationsLoaded } = useComponentTranslations('Hospitality');
    const ht = (key: string, fallback?: string) => {
        if (!translationsLoaded) return fallback ?? key;
        return t(key, fallback) ?? fallback ?? key;
    };

    const expertiseItems = [
        { title: ht('hospitalityExpertise1'), icon: 'carbon:analytics' },
        { title: ht('hospitalityExpertise2'), icon: 'carbon:machine-learning-model' },
        { title: ht('hospitalityExpertise3'), icon: 'carbon:rule' },
        { title: ht('hospitalityExpertise4'), icon: 'carbon:security' },
        { title: ht('hospitalityExpertise5'), icon: 'carbon:cloud-app' },
        { title: ht('hospitalityExpertise6'), icon: 'carbon:blockchain' },
        { title: ht('hospitalityExpertise7'), icon: 'carbon:data-refinery' },
        { title: ht('hospitalityExpertise8'), icon: 'carbon:chart-line-data' },
        { title: ht('hospitalityExpertise9'), icon: 'carbon:user-profile' },
    ];

    // Данные для аккордеона (Примеры решений)
    const useCasesForAccordion = [
        { title: ht('hospitalityUseCase1'), description: ht('hospitalityUseCase1Desc'), backgroundImage: '/images/robot-with-laptop-computer-mouse_1048-3560.jpg' },
        { title: ht('hospitalityUseCase2'), description: ht('hospitalityUseCase2Desc'), backgroundImage: '/images/robot-with-shopping-cart_1048-3574.jpg' },
        { title: ht('hospitalityUseCase3'), description: ht('hospitalityUseCase3Desc'), backgroundImage: '/images/robot-with-clipboard-boxes_1048-3603.jpg' },
        { title: ht('hospitalityUseCase4'), description: ht('hospitalityUseCase4Desc'), backgroundImage: '/images/education_1.jpg' },
    ];

    // Данные для чередующегося макета (Услуги)
    const servicesForAlternating = [
        { title: ht('hospitalityService1Title'), description: ht('hospitalityService1Desc'), image: '/images/finance.jpg' },
        { title: ht('hospitalityService2Title'), description: ht('hospitalityService2Desc'), image: '/images/digital-marketing.jpg' },
        { title: ht('hospitalityService3Title'), description: ht('hospitalityService3Desc'), image: '/images/robot-constructor_1048-4570.jpg' },
        { title: ht('hospitalityService4Title'), description: ht('hospitalityService4Desc'), image: '/images/robot-with-clipboard_1048-3605.jpg' },
        { title: ht('hospitalityService5Title'), description: ht('hospitalityService5Desc'), image: '/images/robot-working-with-computer_1048-4282.jpg' },
    ];

    const faqItems = [
        { question: ht('hospitalityFaq1q'), answer: ht('hospitalityFaq1a') },
        { question: ht('hospitalityFaq2q'), answer: ht('hospitalityFaq2a') },
        { question: ht('hospitalityFaq3q'), answer: ht('hospitalityFaq3a') },
        { question: ht('hospitalityFaq4q'), answer: ht('hospitalityFaq4a') },
        { question: ht('hospitalityFaq5q'), answer: ht('hospitalityFaq5a') },
    ];

    return (
        <div className="relative bg-transparent text-white">
            {/* Hero-блок */}
            <header className="flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-24">
                <motion.h1
                    className="text-3xl lg:text-7xl font-bold tracking-tight mb-6 bg-linear-to-b from-white to-white/90 bg-clip-text text-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    {ht('hospitalityHeroTitle')}
                </motion.h1>
                <motion.p
                    className="text-lg text-white/70 max-w-3xl mx-auto pb-24"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    {ht('hospitalityHeroSubtitle')}
                </motion.p>
            </header>

            {/* Проблемы индустрии */}
            <section className="w-full py-24 bg-white/5 border-y border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center mb-12">{ht('hospitalityPainTitle')}</h2>
                    <p className="text-lg text-white/70 text-center max-w-4xl mx-auto">
                        {ht('hospitalityPainIntro')}
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Наше решение / Expertise */}
                <section className="my-24">
                    <h2 className="text-3xl font-bold text-center mb-12">{ht('hospitalityExpertiseTitle')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {expertiseItems.map((item, index) => (
                            <motion.div
                                key={index}
                                className="p-6 border border-white/10 rounded-lg bg-white/5 flex flex-col items-center text-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Icon icon={item.icon} className="text-4xl mb-4 text-blue-400" />
                                <h3 className="font-semibold">{item.title}</h3>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AlternatingUseCases title={ht('hospitalityServicesTitle')} useCases={servicesForAlternating} />
            </div>

            {/* Accordion with Use Cases */}
            <ServicesAccordion title={ht('hospitalityUseCasesTitle')} services={useCasesForAccordion} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <TechSection title={ht('hospitalityTechTitle')} t={t} />
                <IndustrialsForm label={ht('hospitalityCtaTitle')} />

                {/* FAQ */}
                <section className="my-24 max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12">{ht('hospitalityFaqTitle')}</h2>
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <details key={index} className="p-4 border border-white/10 rounded-lg bg-white/5">
                                <summary className="font-semibold cursor-pointer">{item.question}</summary>
                                <p className="mt-2 text-white/70">{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </section>

            </div>
        </div>
    );
};

export default HospitalityPage;
